/**
 * Единственный HTTP-клиент приложения (MVP_TECH_RFC §22).
 *
 * Отвечает за: cookie-сессию, CSRF-заголовок, request ID, разбор Problem Details,
 * timeout и отмену. Токен авторизации в JS не хранится — он в HttpOnly cookie.
 */

import { randomUuid } from '@shared/lib/uuid';

import { ApiError, parseRetryAfter, toProblemDetail } from './problem';

export const API_PREFIX = '/api/v1';
const DEFAULT_TIMEOUT_MS = 15_000;
/**
 * Имена CSRF-cookie в порядке проверки.
 *
 * По HTTPS backend ставит cookie с префиксом `__Host-` (браузер принимает её
 * только с Secure, Path=/ и без Domain — подменить с поддомена нельзя). По HTTP
 * такая cookie была бы отброшена, поэтому локально префикс снимается. Клиент
 * обязан знать оба имени: иначе в production заголовок не отправится и любой
 * изменяющий запрос получит 403.
 */
const CSRF_COOKIES = ['__Host-carviq_csrf', 'carviq_csrf'] as const;
const CSRF_HEADER = 'X-CSRF-Token';
const REQUEST_ID_HEADER = 'X-Request-ID';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | null {
  // Имя может содержать `-`, поэтому экранируем перед подстановкой в регулярку.
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function readCsrfToken(): string | null {
  for (const name of CSRF_COOKIES) {
    const value = readCookie(name);
    if (value) return value;
  }
  return null;
}

function newRequestId(): string {
  // Не `crypto.randomUUID` напрямую: вне secure context его нет, и запрос
  // падал бы здесь — до `fetch`, то есть молча и без следа в сети.
  return randomUuid();
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeoutMs?: number;
  /** Ключ идемпотентности для критичных POST (создание сессии, confirm, commit). */
  idempotencyKey?: string;
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { body, timeoutMs = DEFAULT_TIMEOUT_MS, idempotencyKey, signal, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();

  const headers = new Headers(rest.headers);
  headers.set('Accept', 'application/json');
  headers.set(REQUEST_ID_HEADER, newRequestId());

  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers.set(CSRF_HEADER, csrf);
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
  }

  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...rest,
      method,
      headers,
      credentials: 'include',
      signal: combinedSignal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError(toProblemDetail(null, 0));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = response.headers.get('content-type')?.includes('json')
    ? await response.json()
    : null;

  if (!response.ok) {
    throw new ApiError(
      toProblemDetail(payload, response.status),
      parseRetryAfter(response.headers.get('Retry-After')),
    );
  }

  return payload as T;
}
